/**
 * The global leaderboard.
 *
 * Every row states what its company has paid, and the order is that figure.
 * The hero states the rule; the board is the evidence.
 *
 * The hero is the Live Board: copy on the left, and on the right the actual top
 * four rows of the actual global board, reshuffling when a payment clears. It
 * is the same data as the full leaderboard further down the page — one query,
 * read twice — so the hero cannot show a position the board below it disagrees
 * with. Nothing in it is illustrative.
 *
 * Behind both sits the rocket layer, which flies the rest of that same read:
 * the listings ranked below the four on display, launching and bursting into
 * banknotes, and a cleared bid launching its own row as it lands. It reads as
 * weather rather than as information, but it is made of the same board, and an
 * empty board leaves an empty sky.
 */

import { Link } from 'react-router-dom';
import { useCallback, useMemo, type ReactNode } from 'react';

import {
  LISTING_LIFETIME_DAYS,
  MINIMUM_FIRST_BID_CENTS,
  PASS_INCREMENT_CENTS,
  type LeaderboardScope,
} from '@bidtohire/shared';

import { BoardSection } from '../components/BoardSection';
import { CategoryGrid } from '../components/CategoryNav';
import { LiveBoard } from '../components/LiveBoard';
import {
  LiveTicker,
  TickerStrip,
  tickerEventsFromEntries,
  useLiveTicker,
} from '../components/LiveTicker';
import { Money } from '../components/Money';
import { RocketField } from '../components/RocketField';
import { ArrowRight, LiveDot, MoneyWhereMouthIs } from '../components/ui';
import { PUBLISH_FROM_LABEL, firstPlaceTotalCents } from '../lib/bid-pricing';
import { boardSalaryStat, compactMoneyLabel } from '../lib/board-stats';
import { useFallbackPolling, useLiveBids } from '../lib/live';
import { useLeaderboard } from '../lib/queries';
import { PricingTrio } from './pricing-shared';

const GLOBAL: LeaderboardScope = { kind: 'global' };

export function HomePage(): ReactNode {
  // The same query the board renders from, so the ticker and the headline
  // figures cost no extra request.
  const board = useLeaderboard(GLOBAL);

  const entries = useMemo(
    () => (board.data?.pages ?? []).flatMap((page) => page.items),
    [board.data],
  );

  // One read, one refetch, shared by the hero board, the activity feed, and the
  // full leaderboard below. A bid pushed over the socket asks for that refetch;
  // no socket means the same refetch on a slow timer instead. Either way, every
  // figure on the page comes from the same read and cannot disagree with
  // itself.
  const { refetch } = board;
  const refresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const { isLive } = useLiveBids({});
  useFallbackPolling(isLive, refresh);

  const seeded = useMemo(() => tickerEventsFromEntries(entries), [entries]);
  const events = useLiveTicker(seeded);

  // A failed or unfinished board read has no figures to report. Printing the
  // fallbacks — "0 listings", "top spot $5" — would state as fact something we
  // have not been told, so those cells hold until the numbers actually arrive.
  const figuresKnown = !board.isError && !board.isLoading;
  const totalListings = board.data?.pages[0]?.totalListings ?? entries.length;
  const topSpot = firstPlaceTotalCents(entries.map((entry) => entry.totalPaidCents));
  const salaryStat = boardSalaryStat(entries);

  return (
    <>
      <section className="hero">
        <RocketField entries={entries} />
        <div className="inner herosplit">
          <div className="hero-copy">
            {/* The rule, not a running total of what the board has taken:
                the figure that means something is the one on each row. */}
            <span className="hero-flag">
              <LiveDot indigo />
              Rank is the total paid.
            </span>

            <h1>They’re bidding on you.</h1>

            <p className="lede">
              Rank is what the company paid. Every listing shows the figure and the salary.
              Applying goes to the company’s own posting — no account, no résumé.
            </p>

            <div className="hero-actions">
              <a className="pill pill-primary" href="#leaderboard">
                Browse the leaderboard
                <ArrowRight />
              </a>
              <Link className="pill pill-ghost" to="/post">
                Post a job — {PUBLISH_FROM_LABEL}
              </Link>
            </div>

            <p className="hero-fineprint">
              {figuresKnown ? (
                <>
                  #1 costs <Money cents={topSpot} /> today.{' '}
                </>
              ) : null}
              Bid any amount from <Money cents={MINIMUM_FIRST_BID_CENTS} />. Rank is the total
              paid, and passing a listing takes its total{' '}
              <span className="mono">
                +&nbsp;
                <Money cents={PASS_INCREMENT_CENTS} />
              </span>
              . Bids are non-refundable.
            </p>
          </div>

          <LiveBoard
            entries={entries}
            totalListings={totalListings}
            isLoading={board.isLoading}
            isError={board.isError}
            onStale={refresh}
          />
        </div>
      </section>

      <TickerStrip events={events} />

      <div className="inner">
        <div className="statstrip" style={{ borderBottom: '1px solid var(--hairline)' }}>
          <div className="stat">
            <span className="eyebrow">Active listings</span>
            <div className="value money is-large">
              {figuresKnown ? totalListings.toLocaleString('en-US') : '—'}
            </div>
          </div>
          <div className="stat">
            <span className="eyebrow">Top spot costs</span>
            <div className="value">
              {figuresKnown ? <Money cents={topSpot} size="large" /> : <span className="money is-large">—</span>}
            </div>
          </div>
          <div className="stat">
            <span className="eyebrow">Salary disclosed</span>
            <div className="value money is-large">100%</div>
          </div>
          <div className="stat">
            {salaryStat.kind === 'median' ? (
              <>
                <span className="eyebrow">Median salary</span>
                <div className="value money is-large">
                  {figuresKnown ? compactMoneyLabel(salaryStat.cents) : '—'}
                </div>
              </>
            ) : (
              <>
                <span className="eyebrow">Companies hiring</span>
                <div className="value money is-large">
                  {figuresKnown ? salaryStat.count.toLocaleString('en-US') : '—'}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <section className="section">
        <div className="inner homesplit">
          <div>
            <div
              className="row"
              style={{ justifyContent: 'space-between', alignItems: 'baseline' }}
            >
              <h2 style={{ fontSize: 19 }}>Browse by category</h2>
              <Link className="small" to="/categories">
                Every category has its own board →
              </Link>
            </div>
            <CategoryGrid limit={7} />
          </div>

          {/* The same stream the hero listens to, stated as a running list of
              what has just cleared rather than as positions on a board. */}
          <LiveTicker events={events} />
        </div>
      </section>

      <section className="section" id="leaderboard">
        <div className="inner">
          <div className="section-heading">
            <div>
              <h2>Global leaderboard</h2>
              <p>
                Ranked by the total each company has paid. Updated as bids clear.
              </p>
            </div>
          </div>
          <BoardSection scope={GLOBAL} scopeName="every category" />
        </div>
      </section>

      <section className="section">
        <div className="inner">
          <PricingTrio />
        </div>
      </section>

      <section className="section is-last">
        <div className="inner">
          <div className="card swagger">
            <div className="line">
              <MoneyWhereMouthIs />
            </div>
            <p>
              <Money cents={MINIMUM_FIRST_BID_CENTS} /> puts a role on the board, and the same
              payment buys its position. Paste your posting and we fill in the details. Listings run{' '}
              {LISTING_LIFETIME_DAYS} days and renew free.
            </p>
            <div className="row" style={{ marginTop: 20 }}>
              <Link className="pill pill-primary" to="/post">
                Post a job — {PUBLISH_FROM_LABEL}
                <ArrowRight />
              </Link>
              <Link className="pill pill-quiet" to="/for-employers">
                For employers
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
